<template>
  <div class="hp_commuEditorWrap happy-editor" :class="{ 'happy-editor--fullscreen': fullscreen, 'happy-editor--blocks': visualblocks }">
    <div class="happy-editor__menu" role="toolbar" aria-label="편집 메뉴">
      <label v-for="menu in menus" :key="menu.label" class="happy-editor__menu-label">
        <span class="happy-editor__sr-only">{{ menu.label }}</span>
        <select :aria-label="menu.label" :disabled="disabled" @change="onMenu($event)">
          <option value="">{{ menu.label }}</option>
          <option v-for="item in menu.items" :key="item[0]" :value="item[0]">{{ item[1] }}</option>
        </select>
      </label>
      <span class="happy-editor__brand">Happytrain Editor</span>
    </div>
    <div class="happy-editor__toolbar" role="toolbar" aria-label="본문 서식">
      <select aria-label="문단 형식" :disabled="disabled" :value="blockType" @change="setBlock($event.target.value)">
        <option value="p">본문</option>
        <option v-for="level in 6" :key="level" :value="'h' + level">제목 {{ level }}</option>
        <option value="blockquote">인용문</option>
        <option value="pre">코드 블록</option>
      </select>
      <select v-if="preset !== 'simple'" aria-label="글자 크기" :disabled="disabled" :value="fontSize" @change="setTextStyle('font-size', $event.target.value)">
        <option value="">기본 크기</option>
        <option v-for="size in sizes" :key="size" :value="size">{{ size }}</option>
      </select>
      <button v-for="button in buttons" :key="button.command" type="button" :title="button.label" :aria-label="button.label" :aria-pressed="isActive(button.command)" :disabled="disabled || !canRun(button.command)" :class="'happy-editor__button--' + button.command" @mousedown.prevent @click="execute(button.command)">{{ button.text }}</button>
      <label class="happy-editor__color" title="글자 색">
        글자 색 <input type="color" aria-label="글자 색" value="#222222" :disabled="disabled" @input="setTextStyle('color', $event.target.value)">
      </label>
      <label class="happy-editor__color" title="배경 색">
        배경 색 <input type="color" aria-label="배경 색" value="#ffff00" :disabled="disabled" @input="setTextStyle('background-color', $event.target.value)">
      </label>
      <button type="button" :disabled="disabled" :aria-pressed="fullscreen" @mousedown.prevent @click="execute('fullscreen')">{{ fullscreen ? '화면 복귀' : '전체 화면' }}</button>
    </div>
    <!-- ProseMirror가 이 요소 내부의 DOM과 한글 조합/선택 영역을 관리한다. -->
    <div ref="surface" class="happy-editor__surface" :style="{ height: fullscreen ? null : height + 'px' }"></div>
    <div class="happy-editor__status" role="status" aria-live="polite">
      <span>{{ uploading ? '이미지 업로드 중 ' + uploading + '개 — 완료 후 저장해 주세요.' : '글자 ' + characterCount + ' · 단어 ' + wordCount }}</span>
      <span>이미지 붙여넣기 / 끌어놓기 지원</span>
    </div>
    <p v-if="error" class="happy-editor__error" role="alert">{{ error }}</p>

    <!-- 네이티브 prompt 대신 키보드 접근과 검증 오류 표시를 지원하는 공통 대화상자. -->
    <div v-if="dialog" class="happy-editor__overlay" @mousedown.self="closeDialog">
      <div ref="dialog" class="happy-editor__dialog" role="dialog" aria-modal="true" :aria-labelledby="inputId + '-dialog-title'" tabindex="-1" @keydown.esc.stop.prevent="closeDialog" @keydown.tab="trapFocus">
        <div class="happy-editor__dialog-heading">
          <h3 :id="inputId + '-dialog-title'">{{ dialog.title }}</h3>
          <button type="button" aria-label="대화상자 닫기" @click="closeDialog">닫기</button>
        </div>
        <div v-if="dialog.type === 'help'" class="happy-editor__help">
          <p>Ctrl/Cmd+B 굵게 · I 기울임 · U 밑줄 · Z 실행 취소 · Shift+Z 다시 실행</p>
          <p>Ctrl/Cmd+K 링크 · Ctrl/Cmd+F 찾기 · Esc 전체 화면 닫기</p>
          <p>표에서 Tab/Shift+Tab으로 셀 이동, 셀들을 드래그한 뒤 표 메뉴에서 병합합니다. 열 경계를 드래그하면 너비를 조절할 수 있습니다.</p>
          <p>줄 앞에서 “- ”, “1. ”, “# ”를 입력하면 목록이나 제목으로 전환됩니다. 한글 입력 조합 중에는 문서를 강제로 다시 그리지 않습니다.</p>
          <p>이미지를 선택한 뒤 이미지 메뉴에서 크기와 설명을 수정합니다. 동영상은 HTTPS 임베드 주소 또는 동영상·오디오 파일 URL을 사용합니다. YouTube/Vimeo 공유 주소는 자동 변환합니다.</p>
          <p>접기 본문을 편집할 때 제목을 눌러 펼쳐 주세요. HTML 소스는 적용 버튼을 눌러야 저장됩니다.</p>
        </div>
        <iframe v-else-if="dialog.type === 'preview'" title="본문 미리보기" class="happy-editor__preview" sandbox="allow-same-origin allow-modals" :srcdoc="previewDocument"></iframe>
        <template v-else>
          <label v-for="field in dialog.fields" :key="field.name" class="happy-editor__field">
            <span>{{ field.label }}</span>
            <textarea v-if="field.type === 'textarea'" v-model="dialog.values[field.name]" :aria-label="field.label" :rows="field.name === 'html' ? 16 : 3" spellcheck="false"></textarea>
            <select v-else-if="field.options" v-model="dialog.values[field.name]" :aria-label="field.label">
              <option v-for="option in field.options" :key="option[0]" :value="option[0]">{{ option[1] }}</option>
            </select>
            <input v-else v-model="dialog.values[field.name]" :type="field.type || 'text'" :aria-label="field.label" :min="field.min" :max="field.max" @keydown.enter.prevent="applyDialog">
          </label>
          <label v-if="dialog.type === 'image'" class="happy-editor__field">
            <span>기기에서 이미지 업로드 (PNG/JPEG/GIF/WebP/AVIF, 최대 20MB)</span>
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/avif" multiple :disabled="uploading > 0" @change="onFileChange">
          </label>
          <p v-if="dialog.type === 'code'" class="happy-editor__hint">위험한 스크립트와 이벤트 속성은 제거됩니다. 기존 HTML을 수정한 후 적용하세요.</p>
          <div v-if="dialog.type === 'charmap'" class="happy-editor__characters">
            <button v-for="character in characters" :key="character" type="button" @click="insertCharacter(character)">{{ character }}</button>
          </div>
        </template>
        <p v-if="dialogError" class="happy-editor__error" role="alert">{{ dialogError }}</p>
        <div class="happy-editor__dialog-actions">
          <template v-if="dialog.type === 'search'">
            <button type="button" @click="search('next')">다음 찾기</button>
            <button type="button" @click="search('replace')">바꾸기</button>
            <button type="button" @click="search('all')">모두 바꾸기</button>
          </template>
          <button v-else-if="['preview', 'help', 'charmap'].indexOf(dialog.type) === -1" type="button" class="happy-editor__primary" @click="applyDialog">적용</button>
          <button v-if="dialog.type === 'preview'" type="button" @click="printPreview">인쇄</button>
          <button type="button" @click="closeDialog">{{ ['preview', 'help'].indexOf(dialog.type) !== -1 ? '닫기' : '취소' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import { EditorState, TextSelection } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { closeHistory, undo, redo } from 'prosemirror-history'
import { setCellAttr } from 'prosemirror-tables'
import { FORMAT_BUTTONS, EDITOR_MENUS, FONT_SIZES } from './customTextareaConfig'
import { editorSchema, parseHtml, serializeHtml } from './editor/schema'
import { sanitizeHtml, safeUrl, mediaUrl } from './editor/html'
import { createPlugins, runCommand, markActive, currentMark, textStyle, changeBlock, insertHtml, makeTable, findMatches, selectMatch, ancestor, styleAttributes, linkRange, tableCommands } from './editor/commands'
import { insert_editor_image, base_url_export } from './Axios.vue'
import './editor/editor.css'

const field = (name, label, type = 'text', options) => ({ name, label, type, options })

export default {
  name: 'CustomTextarea',
  inheritAttrs: false,
  props: {
    value: { type: String, default: '' },
    preset: { type: String, default: 'default' },
    height: { type: Number, default: 300 },
    id: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return {
      buttons: FORMAT_BUTTONS,
      menus: EDITOR_MENUS,
      sizes: FONT_SIZES,
      revision: 0,
      fullscreen: false,
      visualblocks: false,
      uploading: 0,
      error: '',
      dialog: null,
      dialogError: '',
      previewHtml: '',
      characters: ['©', '®', '™', '€', '£', '¥', '₩', '°', '±', '×', '÷', '→', '←', '↑', '↓', '♥', '★', '☆', '※', '·', '…', '“', '”', '‘', '’']
    }
  },
  computed: {
    inputId () { return this.id || 'happy-editor-' + this._uid },
    characterCount () { return this.documentText.replace(/\s/g, '').length },
    wordCount () { return this.documentText.trim() ? this.documentText.trim().split(/\s+/).length : 0 },
    documentText () {
      // 엔진 인스턴스는 Vue 반응형 객체에 넣지 않고 revision으로 UI만 갱신한다.
      void this.revision
      return this.editorView ? this.editorView.state.doc.textBetween(0, this.editorView.state.doc.content.size, '\n') : ''
    },
    blockType () {
      void this.revision
      if (!this.editorView) return 'p'
      const node = this.editorView.state.selection.$from.parent
      return node.type.name === 'heading' ? 'h' + node.attrs.level : node.type.name === 'code_block' ? 'pre' : 'p'
    },
    fontSize () {
      void this.revision
      if (!this.editorView) return ''
      const mark = currentMark(this.editorView.state, 'text_style')
      const element = document.createElement('span')
      element.style.cssText = mark ? mark.attrs.htmlAttrs.style || '' : ''
      return element.style.fontSize
    },
    previewDocument () {
      // sandbox iframe이 사이트 전역 CSS와 본문 스타일을 서로 격리한다.
      return '<!doctype html><html lang="ko"><head><meta charset="utf-8"><style>body{font:14px Helvetica,Arial,sans-serif;line-height:1.6;padding:20px;overflow-wrap:anywhere}img,video,iframe{max-width:100%}table{border-collapse:collapse}td,th{padding:8px}blockquote{border-left:3px solid #cbd5e1;padding-left:16px}details{padding:8px;border:1px solid #cbd5e1}</style></head><body>' + this.previewHtml + '</body></html>'
    }
  },
  watch: {
    value (newValue) {
      if (!this.editorView || newValue === this.lastEmittedHtml) return
      const doc = parseHtml(newValue)
      if (doc.eq(this.editorView.state.doc)) return
      // 다른 게시글/일정으로 바뀔 때 이전 글의 undo 기록과 업로드를 이어 붙이지 않는다.
      this.documentVersion++
      if (this.dialog) this.closeDialog()
      this.editorView.updateState(EditorState.create({ schema: editorSchema, doc, plugins: createPlugins() }))
      this.revision++
    },
    disabled () {
      if (this.editorView) this.editorView.setProps({ editable: () => !this.disabled })
    },
    inputId (id) {
      if (this.editorView) this.editorView.setProps({ attributes: { ...this.editorView.props.attributes, id } })
    }
  },
  mounted () {
    this.pendingUploads = []
    this.documentVersion = 0
    this.editorView = new EditorView(this.$refs.surface, {
      state: EditorState.create({ schema: editorSchema, doc: parseHtml(this.value), plugins: createPlugins() }),
      editable: () => !this.disabled,
      attributes: { id: this.inputId, role: 'textbox', 'aria-label': this.$attrs['aria-label'] || '내용', 'aria-multiline': 'true', 'data-placeholder': '내용을 입력하세요.' },
      dispatchTransaction: this.dispatchTransaction,
      transformPastedHTML: sanitizeHtml,
      handlePaste: this.handlePaste,
      handleDrop: this.handleDrop,
      handleKeyDown: (view, event) => {
        if ((event.ctrlKey || event.metaKey) && !event.altKey && ['k', 'f'].indexOf(event.key.toLowerCase()) !== -1) {
          event.preventDefault()
          this.execute(event.key.toLowerCase() === 'k' ? 'link' : 'search')
          return true
        }
        return false
      }
    })
    this.revision++
    document.addEventListener('keydown', this.onEscape)
  },
  beforeDestroy () {
    this.documentVersion++
    document.removeEventListener('keydown', this.onEscape)
    if (this.editorView) this.editorView.destroy()
    this.editorView = null
  },
  methods: {
    dispatchTransaction (transaction) {
      const result = this.editorView.state.applyTransaction(transaction)
      // 업로드 중 입력/삭제/표 편집이 일어나도 요청 당시의 삽입 위치를 추적한다.
      result.transactions.forEach(tr => {
        this.pendingUploads.forEach(upload => { upload.bookmark = upload.bookmark.map(tr.mapping) })
      })
      this.editorView.updateState(result.state)
      this.revision++
      if (result.transactions.some(tr => tr.docChanged)) {
        this.lastEmittedHtml = serializeHtml(result.state.doc)
        this.$emit('input', this.lastEmittedHtml)
        this.$emit('change', this.lastEmittedHtml)
      }
    },
    isActive (command) {
      void this.revision
      if (!this.editorView) return false
      const state = this.editorView.state
      if (editorSchema.marks[command]) return markActive(state, command)
      if (['bullet_list', 'ordered_list'].indexOf(command) !== -1) return !!ancestor(state, [command])
      if (['left', 'center', 'right', 'justify'].indexOf(command) !== -1) {
        const attrs = state.selection.$from.parent.attrs.htmlAttrs || {}
        const element = document.createElement('p')
        element.style.cssText = attrs.style || ''
        return (element.style.textAlign || attrs.align || 'left') === command
      }
      return false
    },
    canRun (command) {
      void this.revision
      if (!this.editorView) return false
      if (command === 'undo') return undo(this.editorView.state)
      if (command === 'redo') return redo(this.editorView.state)
      return true
    },
    onMenu (event) {
      const command = event.target.value
      event.target.value = ''
      if (command) this.execute(command)
    },
    execute (command) {
      if (!this.editorView || this.disabled) return
      this.error = ''
      if (command === 'fullscreen') { this.fullscreen = !this.fullscreen; return }
      if (command === 'visualblocks') { this.visualblocks = !this.visualblocks; return }
      if (command.indexOf('format:') === 0) {
        this.setBlock(command.slice(7))
      } else if (command === 'datetime') {
        this.editorView.dispatch(this.editorView.state.tr.insertText(new Date().toLocaleString('ko-KR')))
      } else if (command === 'toggle') {
        insertHtml(this.editorView.state, this.editorView.dispatch, '<details open><summary>자세히 보기</summary><p>접을 내용을 입력하세요.</p></details><p></p>')
      } else if (['image', 'link', 'anchor', 'media', 'charmap', 'table', 'tableProperties', 'cellProperties', 'code', 'preview', 'print', 'search', 'help'].indexOf(command) !== -1) {
        this.openDialog(command === 'print' ? 'preview' : command)
        return
      } else if (!runCommand(command, this.editorView.state, this.editorView.dispatch)) {
        this.error = tableCommands[command] ? '표 안의 셀을 선택하세요. 셀 합치기는 서로 붙어 있는 셀을 여러 개 선택해야 합니다.' : '현재 선택 영역에서는 이 명령을 적용할 수 없습니다.'
      }
      this.editorView.focus()
    },
    setBlock (value) {
      changeBlock(value, this.editorView.state, this.editorView.dispatch)
      this.editorView.focus()
    },
    setTextStyle (property, value) {
      if (this.disabled) return
      textStyle(property, value)(this.editorView.state, this.editorView.dispatch)
      this.editorView.focus()
    },
    openDialog (type) {
      const state = this.editorView.state
      let title = ''
      let fields = []
      let values = {}
      this.dialogTarget = null
      this.dialogReturnFocus = document.activeElement
      this.dialogError = ''
      if (type === 'image') {
        title = '이미지 삽입 / 수정'
        fields = [field('src', '이미지 주소'), field('alt', '이미지 설명'), field('width', '너비 (숫자, px 또는 %)'), field('height', '높이 (숫자, px 또는 %)')]
        const node = state.selection.node
        if (node && node.type === editorSchema.nodes.image) {
          this.dialogTarget = state.selection.from
          values = { ...node.attrs.htmlAttrs }
          const imageStyle = document.createElement('img').style
          imageStyle.cssText = values.style || ''
          values.width = imageStyle.width || values.width || ''
          values.height = imageStyle.height || values.height || ''
        }
      } else if (type === 'link') {
        title = '링크 삽입 / 수정'
        fields = [field('href', '주소 (https://, mailto:, tel:, #책갈피)'), field('text', '표시할 글자'), field('target', '열기 방식', 'text', [['_self', '현재 창'], ['_blank', '새 창']])]
        const mark = currentMark(state, 'link')
        const range = linkRange(state)
        values = { ...(mark ? mark.attrs.htmlAttrs : {}), target: mark ? mark.attrs.htmlAttrs.target || '_self' : '_self', text: state.doc.textBetween(range.from, range.to, '') }
        this.dialogLinkRange = range
      } else if (type === 'anchor') {
        title = '책갈피 삽입'
        fields = [field('id', '책갈피 이름 (영문/숫자/밑줄/하이픈)')]
      } else if (type === 'media') {
        title = '동영상 / 오디오'
        fields = [field('kind', '형식', 'text', [['iframe', '외부 영상 임베드 (HTTPS)'], ['video', '동영상 파일'], ['audio', '오디오 파일']]), field('src', '주소'), field('width', '너비 (숫자, px 또는 %)'), field('height', '높이 (숫자, px 또는 %)')]
        values = { kind: 'iframe', width: '560', height: '315' }
        const node = state.selection.node
        if (node && ['iframe', 'media'].indexOf(node.type.name) !== -1) {
          this.dialogTarget = state.selection.from
          values = { ...values, ...node.attrs.htmlAttrs, kind: node.type.name === 'iframe' ? 'iframe' : node.attrs.tag }
        }
      } else if (type === 'table') {
        title = '표 삽입'
        fields = [field('rows', '행 수 (1~50)', 'number'), field('columns', '열 수 (1~20)', 'number')]
        values = { rows: 3, columns: 3 }
      } else if (type === 'tableProperties' || type === 'cellProperties') {
        title = type === 'tableProperties' ? '표 속성' : '셀 속성'
        const target = ancestor(state, type === 'tableProperties' ? ['table'] : ['table_cell', 'table_header'])
        if (!target) { this.error = '먼저 표 안의 셀을 선택하세요.'; return }
        this.dialogTarget = target.pos
        const element = document.createElement('div')
        element.style.cssText = target.node.attrs.htmlAttrs.style || ''
        fields = [field('width', '너비 (예: 100%, 300px)'), field('background', '배경 색 (예: #ffffff)'), field('border', '테두리 (예: 1px solid #cbd5e1)'), field('align', '정렬', 'text', [['', '기본'], ['left', '왼쪽'], ['center', '가운데'], ['right', '오른쪽']])]
        values = { width: element.style.width, background: element.style.backgroundColor, border: element.style.border, align: element.style.textAlign || '' }
      } else if (type === 'code') {
        title = 'HTML 소스 편집'
        fields = [field('html', 'HTML 소스', 'textarea')]
        values = { html: serializeHtml(state.doc) }
      } else if (type === 'search') {
        title = '찾기 / 바꾸기'
        fields = [field('query', '찾을 글자'), field('replacement', '바꿀 글자'), field('matchCase', '대소문자', 'text', [['no', '구분 안 함'], ['yes', '구분']])]
        values = { query: state.doc.textBetween(state.selection.from, state.selection.to, ''), replacement: '', matchCase: 'no' }
      } else if (type === 'charmap') title = '특수문자 삽입'
      else if (type === 'preview') { title = '본문 미리보기'; this.previewHtml = serializeHtml(state.doc) } else title = '에디터 사용 방법'
      fields.forEach(item => { if (values[item.name] === undefined) values[item.name] = '' })
      this.dialog = { type, title, fields, values }
      this.$nextTick(() => {
        const focusable = this.$refs.dialog.querySelector('textarea, input, select')
        ;(focusable || this.$refs.dialog).focus()
      })
    },
    closeDialog () {
      const focusTarget = this.dialogReturnFocus
      this.dialog = null
      this.dialogError = ''
      this.$nextTick(() => {
        if (focusTarget && document.contains(focusTarget) && focusTarget.focus) focusTarget.focus()
        else if (this.editorView) this.editorView.focus()
      })
    },
    trapFocus (event) {
      const items = Array.from(this.$refs.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), select, textarea, iframe'))
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.$refs.dialog)) { event.preventDefault(); last.focus() } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    },
    onEscape (event) {
      if (event.key === 'Escape' && !this.dialog && this.fullscreen) this.fullscreen = false
    },
    applyDialog () {
      if (!this.dialog) return
      const { type, values } = this.dialog
      const state = this.editorView.state
      let tr = state.tr
      this.dialogError = ''
      if (type === 'image' || type === 'media') {
        const src = type === 'media' && values.kind === 'iframe' ? mediaUrl(values.src) : safeUrl(values.src, 'image')
        if (!src) { this.dialogError = '유효한 주소를 입력하세요. 외부 영상 임베드는 HTTPS 주소를 사용하세요.'; return }
        for (const dimension of ['width', 'height']) {
          if (values[dimension] && (!/^\d+(?:\.\d+)?(?:px|%)?$/.test(String(values[dimension])) || parseFloat(values[dimension]) <= 0 || parseFloat(values[dimension]) > 10000)) {
            this.dialogError = '크기는 0보다 크고 10000 이하인 숫자, px 또는 %로 입력하세요.'; return
          }
        }
        const previous = this.dialogTarget === null ? null : state.doc.nodeAt(this.dialogTarget)
        let htmlAttrs = { ...(previous ? previous.attrs.htmlAttrs : {}), src }
        ;['width', 'height'].forEach(name => {
          const value = String(values[name] || '')
          const cssValue = value && /^\d+(?:\.\d+)?$/.test(value) ? value + 'px' : value
          // 예전 style.width가 새 width 속성을 덮어쓰지 않도록 두 표현을 함께 갱신한다.
          htmlAttrs = styleAttributes(htmlAttrs, { [name]: cssValue })
          if (value && !/%$/.test(value)) htmlAttrs[name] = String(parseFloat(value))
          else delete htmlAttrs[name]
        })
        const kind = type === 'image' ? 'image' : values.kind === 'iframe' ? 'iframe' : 'media'
        if (type === 'image') htmlAttrs.alt = values.alt
        if (kind === 'media') htmlAttrs.controls = ''
        if (kind === 'iframe') htmlAttrs.title = '동영상'
        const node = editorSchema.nodes[kind].create({ htmlAttrs, ...(kind === 'media' ? { tag: values.kind, sources: [] } : {}) })
        tr = previous ? tr.replaceWith(this.dialogTarget, this.dialogTarget + previous.nodeSize, node) : tr.replaceSelectionWith(node)
      } else if (type === 'link') {
        const href = safeUrl(values.href)
        if (!href) { this.dialogError = '유효한 링크 주소를 입력하세요.'; return }
        const range = this.dialogLinkRange
        const text = values.text || href
        const link = editorSchema.marks.link.create({ htmlAttrs: { href, target: values.target, ...(values.target === '_blank' ? { rel: 'noopener noreferrer' } : {}) } })
        const original = state.doc.textBetween(range.from, range.to, '')
        tr = tr.setSelection(TextSelection.create(state.doc, range.from, range.to))
        if (range.from !== range.to && original === text) tr = tr.addMark(range.from, range.to, link)
        else tr = tr.replaceSelectionWith(editorSchema.text(text, [link]), false)
        tr = tr.removeStoredMark(editorSchema.marks.link)
      } else if (type === 'anchor') {
        if (!/^[A-Za-z][\w-]*$/.test(values.id) || this.editorView.dom.querySelector('[id="' + values.id + '"]')) {
          this.dialogError = '영문으로 시작하는 고유한 책갈피 이름을 입력하세요.'; return
        }
        // 빈 링크 마크는 모델에서 사라지므로 현재 문단에 책갈피 ID를 준다.
        const target = ancestor(state, ['paragraph', 'heading', 'summary', 'code_block'])
        if (!target) { this.dialogError = '책갈피를 넣을 문단을 선택하세요.'; return }
        tr = tr.setNodeMarkup(target.pos, null, { ...target.node.attrs, htmlAttrs: { ...target.node.attrs.htmlAttrs, id: values.id } })
      } else if (type === 'table') {
        const rows = Number(values.rows)
        const columns = Number(values.columns)
        if (!Number.isInteger(rows) || rows < 1 || rows > 50 || !Number.isInteger(columns) || columns < 1 || columns > 20) {
          this.dialogError = '행은 1~50, 열은 1~20 사이의 정수로 입력하세요.'; return
        }
        tr = tr.replaceSelectionWith(makeTable(rows, columns))
      } else if (type === 'tableProperties' || type === 'cellProperties') {
        const node = state.doc.nodeAt(this.dialogTarget)
        const htmlAttrs = styleAttributes(node.attrs.htmlAttrs, { width: values.width, 'background-color': values.background, border: values.border, 'text-align': values.align })
        if (type === 'cellProperties') {
          // 여러 셀을 선택했으면 선택한 모든 셀에 같은 속성을 적용한다.
          setCellAttr('htmlAttrs', htmlAttrs)(state, this.editorView.dispatch)
          this.closeDialog(); return
        }
        tr = tr.setNodeMarkup(this.dialogTarget, null, { ...node.attrs, htmlAttrs })
      } else if (type === 'code') {
        tr = tr.replaceWith(0, state.doc.content.size, parseHtml(values.html).content)
      } else return
      this.editorView.dispatch(closeHistory(tr).scrollIntoView())
      this.closeDialog()
    },
    insertCharacter (character) {
      this.editorView.dispatch(this.editorView.state.tr.insertText(character))
      this.closeDialog()
    },
    search (action) {
      const { query, replacement, matchCase } = this.dialog.values
      if (!query) { this.dialogError = '찾을 글자를 입력하세요.'; return }
      const state = this.editorView.state
      const matches = findMatches(state.doc, query, matchCase === 'yes')
      if (!matches.length) { this.dialogError = '일치하는 글자가 없습니다.'; return }
      if (action === 'all') {
        const tr = state.tr
        // 뒤에서부터 교체하여 앞쪽 위치가 밀리는 문제를 방지한다.
        matches.slice().reverse().forEach(match => tr.insertText(replacement, match.from, match.to))
        this.editorView.dispatch(closeHistory(tr))
        this.dialogError = matches.length + '개를 바꿨습니다.'
        return
      }
      if (action === 'replace') {
        const selected = matches.find(match => match.from === state.selection.from && match.to === state.selection.to)
        if (selected) this.editorView.dispatch(closeHistory(state.tr.insertText(replacement, selected.from, selected.to)))
      }
      const updatedState = this.editorView.state
      const remaining = findMatches(updatedState.doc, query, matchCase === 'yes')
      if (remaining.length) {
        const next = remaining.find(match => match.from >= updatedState.selection.to) || remaining[0]
        selectMatch(this.editorView, next)
        this.dialogError = '일치 ' + remaining.length + '개 · ' + (remaining.indexOf(next) + 1) + '번째 선택됨'
      } else this.dialogError = '모두 바꿨습니다.'
    },
    printPreview () {
      const frame = this.$refs.dialog.querySelector('iframe')
      if (frame && frame.contentWindow) { frame.contentWindow.focus(); frame.contentWindow.print() }
    },
    onFileChange (event) {
      const files = Array.from(event.target.files || [])
      if (files.length) {
        this.uploadFiles(files)
        this.closeDialog()
      }
    },
    handlePaste (view, event) {
      if (this.disabled) return false
      const files = Array.from(event.clipboardData ? event.clipboardData.files : [])
      if (files.length) { this.uploadFiles(files); return true }
      const text = event.clipboardData ? event.clipboardData.getData('text/plain') : ''
      const html = event.clipboardData ? event.clipboardData.getData('text/html') : ''
      if (!html && /^https?:\/\/[^\s<>]+$/.test(text)) {
        const mark = editorSchema.marks.link.create({ htmlAttrs: { href: text } })
        view.dispatch(view.state.tr.replaceSelectionWith(editorSchema.text(text, [mark]), false))
        return true
      }
      return false
    },
    handleDrop (view, event) {
      if (this.disabled || !event.dataTransfer || !event.dataTransfer.files.length) return false
      const at = view.posAtCoords({ left: event.clientX, top: event.clientY })
      const selection = at ? TextSelection.near(view.state.doc.resolve(at.pos)) : view.state.selection
      this.uploadFiles(Array.from(event.dataTransfer.files), selection.getBookmark())
      return true
    },
    async uploadFiles (files, bookmark) {
      const upload = { bookmark: bookmark || this.editorView.state.selection.getBookmark(), version: this.documentVersion }
      this.pendingUploads.push(upload)
      this.uploading += files.length
      this.$emit('upload-state', true)
      this.error = ''
      for (const file of files) {
        try {
          if (!/^image\/(png|jpeg|gif|webp|avif)$/.test(file.type) || file.size > 20 * 1024 * 1024) throw new Error('PNG/JPEG/GIF/WebP/AVIF 파일을 20MB 이하로 선택하세요.')
          // 기존 blobInfo 계약에 맞춘 어댑터이므로 서버 필드/URL을 바꾸지 않는다.
          const path = await insert_editor_image({ blob: () => file })
          if (!this.editorView || upload.version !== this.documentVersion) continue
          if (typeof path !== 'string' || !path.trim()) throw new Error('서버에서 이미지 경로를 받지 못했습니다.')
          const src = safeUrl(new URL(path.replace(/\\/g, '/'), base_url_export).href, 'image')
          if (!src) throw new Error('서버에서 유효하지 않은 이미지 주소를 받았습니다.')
          const state = this.editorView.state
          const selected = upload.bookmark.resolve(state.doc)
          const image = editorSchema.nodes.image.create({ htmlAttrs: { src, alt: file.name } })
          const tr = state.tr.setSelection(selected).replaceSelectionWith(image)
          // 업로드를 기다리는 동안 다른 곳에 옮긴 커서는 그대로 유지한다.
          if (!selected.eq(state.selection)) tr.setSelection(state.selection.map(tr.doc, tr.mapping))
          this.editorView.dispatch(closeHistory(tr))
        } catch (error) {
          if (this.editorView && upload.version === this.documentVersion) this.error = file.name + ': ' + ((error.response && error.response.data && error.response.data.message) || error.message || '이미지를 업로드하지 못했습니다. 다시 시도하세요.')
        } finally { this.uploading-- }
      }
      this.pendingUploads = this.pendingUploads.filter(item => item !== upload)
      if (!this.uploading && this.error === '이미지 업로드가 끝난 뒤 다시 저장해 주세요.') this.error = ''
      if (!this.uploading) this.$emit('upload-state', false)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/ETC/CustomTextarea.vue